// path: src/Home.js
import React, { useMemo } from "react";
import Login from "./Login";
import "./App.css";
import AppFooter from "./Components/AppFooter";
import {
    ENDLESS_UNLOCK_STORY_LEVEL,
    STORE_UNLOCK_STORY_LEVEL,
    hasCompletedStoryLevel,
    isEndlessHighScoreUnlocked,
    isEndlessModeUnlocked,
    isStoreUnlocked,
    loadStoryProgress,
} from "./utils/storyProgress";
import { getAuthAccountLabel } from "./utils/authDisplay";
import LockIcon from "./Components/LockIcon";

const Home = ({
    onStoryMode,
    onEndlessMode,
    onProfile,
    onFriends,
    onLeaderboards,
    onStore,
    onHelp,
    onPrivacyPolicy,
    onTermsOfService,
    onLoginSuccess,
    onLogout,
    isAuthenticated,
    user,
    username,
    storyProgress,
}) => {

    const progress = useMemo(() => {
        return storyProgress || loadStoryProgress();
    }, [storyProgress]);

    const tipsUnlocked = hasCompletedStoryLevel(1, progress);
    const endlessUnlocked = isEndlessModeUnlocked(progress);
    const highScoresUnlocked = isEndlessHighScoreUnlocked(progress);
    const storeUnlocked = isStoreUnlocked(progress);

    const handleHelpClick = () => {
        if (!tipsUnlocked) {
            return;
        }
        if (typeof onHelp === "function") {
            onHelp();
        }
    };

    const handleEndlessClick = () => {
        if (!endlessUnlocked) {
            return;
        }
        if (typeof onEndlessMode === "function") {
            onEndlessMode();
        }
    };

    const handleLeaderboardsClick = () => {
        if (!highScoresUnlocked) {
            return;
        }
        if (typeof onLeaderboards === "function") {
            onLeaderboards();
        }
    };

    const handleStoreClick = () => {
        if (!storeUnlocked) {
            return;
        }
        if (typeof onStore === "function") {
            onStore();
        }
    };

    return (
        <div className="homeScreen">
            <img
                className="homeScreen__art"
                src={`${process.env.PUBLIC_URL}/images/home-screen-bg.jpg`}
                alt=""
                aria-hidden="true"
                decoding="async"
            />
            <div className="homeContent">
                <img
                    src="images/home-hero.png"
                    alt="Memory Mix-Up"
                    className="homeImage"
                />

                <h1 className="homeTitle impactFont">Memory Mix-Up</h1>

                <p className="homeModePrompt">
                    {endlessUnlocked
                        ? "Start the Story adventure, or jump into a custom Endless run."
                        : "Start with Story Mode — Endless unlocks as you clear early stages."}
                </p>

                <div className="homeButtons homeButtons--play">
                    <button
                        className="homePrimaryButton homePrimaryButton--hero"
                        onClick={onStoryMode}
                    >
                        Story Mode
                    </button>

                    <button
                        className={`homePrimaryButton homeSecondaryButton${
                            endlessUnlocked ? "" : " homePrimaryButton--locked"
                        }`}
                        onClick={handleEndlessClick}
                        disabled={!endlessUnlocked}
                        aria-disabled={!endlessUnlocked}
                        title={
                            endlessUnlocked
                                ? "Custom Endless runs"
                                : `Clear Story Level ${ENDLESS_UNLOCK_STORY_LEVEL} to unlock Endless Mode`
                        }
                    >
                        {endlessUnlocked ? (
                            "Endless Mode"
                        ) : (
                            <span className="homeLockedLabel">
                                Endless Mode
                                <LockIcon className="homeLockIcon" />
                            </span>
                        )}
                    </button>
                    {!endlessUnlocked ? (
                        <div className="homeModeLockHint">
                            Unlocks after Story Level{" "}
                            {ENDLESS_UNLOCK_STORY_LEVEL}
                        </div>
                    ) : null}
                </div>

                <div
                    className="homeButtons homeButtons--meta"
                    aria-label="Account and extras"
                >
                    <button
                        type="button"
                        className="homeMetaButton"
                        onClick={onProfile}
                    >
                        Profile
                    </button>

                    <button
                        type="button"
                        className="homeMetaButton"
                        onClick={onFriends}
                    >
                        Friends
                    </button>

                    <button
                        type="button"
                        className={`homeMetaButton${
                            highScoresUnlocked ? "" : " homeMetaButton--locked"
                        }`}
                        onClick={handleLeaderboardsClick}
                        disabled={!highScoresUnlocked}
                        aria-disabled={!highScoresUnlocked}
                        title={
                            highScoresUnlocked
                                ? "Endless leaderboards"
                                : `Clear Story Level ${ENDLESS_UNLOCK_STORY_LEVEL} to unlock High Scores`
                        }
                    >
                        {highScoresUnlocked ? (
                            "Leaderboards"
                        ) : (
                            <span className="homeLockedLabel">
                                Leaderboards
                                <LockIcon className="homeLockIcon" />
                            </span>
                        )}
                    </button>

                    {typeof onStore === "function" ? (
                        <button
                            type="button"
                            className={`homeMetaButton${
                                storeUnlocked ? "" : " homeMetaButton--locked"
                            }`}
                            onClick={handleStoreClick}
                            disabled={!storeUnlocked}
                            aria-disabled={!storeUnlocked}
                            title={
                                storeUnlocked
                                    ? "Corgi Packs and hearts"
                                    : `Clear Story Level ${STORE_UNLOCK_STORY_LEVEL} to unlock the Store`
                            }
                        >
                            {storeUnlocked ? (
                                "Store"
                            ) : (
                                <span className="homeLockedLabel">
                                    Store
                                    <LockIcon className="homeLockIcon" />
                                </span>
                            )}
                        </button>
                    ) : null}

                    {!isAuthenticated ? (
                        <div className="homeMetaLogin">
                            <Login onSuccess={onLoginSuccess} />
                        </div>
                    ) : null}
                </div>

                <div className="homeAccountFooter">
                    <div className="homeHint">
                        {!isAuthenticated
                            ? "Login is optional for play — Friends requires a signed-in account."
                            : `Logged in as ${getAuthAccountLabel(user, username)}`}
                    </div>

                    <div className="homeAccountFooter__actions">
                        <button
                            type="button"
                            className="homeLogoutButton"
                            onClick={handleHelpClick}
                            disabled={!tipsUnlocked}
                            aria-disabled={!tipsUnlocked}
                            title={
                                tipsUnlocked
                                    ? "Helpful Tips and mechanic refreshers"
                                    : "Unlocks after clearing Story Level 1"
                            }
                        >
                            Help
                        </button>
                        {isAuthenticated ? (
                            <button
                                type="button"
                                className="homeLogoutButton"
                                onClick={onLogout}
                            >
                                Logout
                            </button>
                        ) : null}
                    </div>

                    {!tipsUnlocked && (
                        <div className="homeTipsHint">
                            Unlocks after Story Level 1
                        </div>
                    )}
                </div>
            </div>
            <AppFooter
                variant="overlay"
                onPrivacyPolicy={onPrivacyPolicy}
                onTermsOfService={onTermsOfService}
            />
        </div>
    );
};

export default Home;
